import videojs from 'video.js';
import { v4 as uuid } from 'uuid';
import LAYOUTS from './smartzoom/layouts.js';
import { calcDimensions } from '../../util';
import { QueryStringParamProvider } from 'PLAYER_SRC/implementations/player-page/helpers.js';

import './smartzoom.css';

const _wrapper = new WeakMap();
const _controlsOverlay = new WeakMap();
const _clickToExit = new WeakMap();
const _canvas = new WeakMap();
const _target = new WeakMap();
const _aspect = new WeakMap();
let localize;

/**
 * The Smartzoom module.
 * @member {SmartzoomPlugin} smartzoom
 * @memberof MediasitePlayer#
 */

/**
 * Allows the user to zoom into specific regions within the video. Works by resizing the <video> element and cropping away the non-zoomed areas with CSS <code>clip-path</code>.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class SmartzoomPlugin extends videojs.getPlugin('plugin') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} [options]
     */
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        // Temp, for ease of testing:
        const params = new URLSearchParams(location.search);
        const presetName = params.get('smartzoom');
        const testRegions = LAYOUTS[presetName] ?? [];

        this.regionCount = 0;
        this.activeRegion = null;
        this.lastFocused = null;

        this.on(player, 'playerresize', this.#handlePlayerResize);

        this.windowResizeListener = () => this.#handlePlayerResize();
        window.addEventListener('resize', this.windowResizeListener);

        const Component = videojs.getComponent('Component');
        const component = new Component(player, { name: 'SmartZoom' });
        component.addClass('mediasite-player__smartzoom-wrapper');
        const paramProvider = new QueryStringParamProvider(location.search);
        if (paramProvider.getParam('DisableInteractiveOverlays')?.toLowerCase() === 'true') {
            component.addClass('mediasite-player__disabled-interactive');
        }
        _wrapper.set(this, component);

        const canvas = document.createElement('canvas');
        canvas.classList.add('mediasite-player__smartzoom-canvas');
        _canvas.set(this, canvas);
        component.el().appendChild(canvas);

        const smartzoomContainer = document.createElement('div');
        smartzoomContainer.classList.add('mediasite-player__smartzoom-controls-overlay');
        _controlsOverlay.set(this, smartzoomContainer);

        component.el().appendChild(smartzoomContainer);

        const clickToExit = document.createElement('div');
        _clickToExit.set(this, clickToExit);
        clickToExit.classList.add('mediasite-player__smart-zoom-exit-container');
        clickToExit.addEventListener('click', (...args) => this.#handleClickToExit(...args));
        clickToExit.hidden = true;

        const clickToExitButton = document.createElement('button');
        clickToExitButton.classList.add('mediasite-player__smart-zoom-exit-button')
        clickToExitButton.addEventListener('keydown', (...args) => this.#handleClickToExitKeydown(...args));
        clickToExitButton.setAttribute('aria-label', localize('Close'));
        clickToExit.appendChild(clickToExitButton);

        component.el().appendChild(clickToExit);

        player.ready(() => {
            player.layout().addChild(component);
            this.#moveComponentAfterVideo();

            // Default to the main video element as a target
            this.target = player.tech(true).el();

            // Update dimensions
            this.#handlePlayerResize();
        });

        player.one('loadedmetadata', () => {
            for (let i = 0; i < testRegions.length; i++) {
                const current = testRegions[i];
                this.addRegion(current);
            }
        });

        player.one(['loadeddata', 'loadedmetadata'], () => {
            if (isNaN(this.aspect) || !this.aspect) {
                this.aspect = player.videoWidth() / player.videoHeight();
            }
        });
    }

    /**
     * The HTML element that will be resized when the user clicks a clickable region.
     * @type {HTMLElement}
     * @example
     * player.smartzoom.target = someHtmlElementWithinPlayer;
     */
    get target() {
        return _target.get(this);
    }

    set target(target) {
        const oldTarget = _target.get(this);
        if (oldTarget) {
            oldTarget.classList.remove('mediasite-player__smartzoomable');
        }

        target?.classList?.add('mediasite-player__smartzoomable');
        _target.set(this, target);
        this.#handlePlayerResize();
    }

    /**
     * The aspect ratio of the current presentation. Used when calculating content dimensions during resizing.
     * @type {number}
     * @example
     * player.smartzoom.aspect = 16 / 9;
     */
    get aspect() {
        return _aspect.get(this);
    }

    set aspect(aspect) {
        _aspect.set(this, aspect);
        this.#handlePlayerResize();
    }

    /** @private */
    #handlePlayerResize() {
        try {
            const controlsOverlay = _controlsOverlay.get(this);

            if (controlsOverlay) {
                const wrapperEl = _wrapper.get(this).el();
                let targetAspect = _aspect.get(this);

                const region = this.activeRegion;
                const size = calcDimensions(wrapperEl, targetAspect, region);

                const targetHeight = size.height;
                const targetWidth = size.width;
                const scale = size.scale;

                const xOffset = region ? region.left : 0;
                const yOffset = region ? region.top : 0;
                const clipWidth = region ? region.width : 1;
                const clipHeight = region ? region.height : 1;

                const targetEl = _target.get(this);
                targetEl.style.setProperty('--video-width', isNaN(targetWidth) ? '100%' : `${targetWidth}px`);
                targetEl.style.setProperty('--video-height', isNaN(targetHeight) ? '100%' : `${targetHeight}px`);

                //allows other plugins to understand the height and width of what smartzoom is interacting with
                targetEl.parentElement.style.setProperty('--video-width', isNaN(targetWidth) ? '100%' : `${targetWidth}px`);
                targetEl.parentElement.style.setProperty('--video-height', isNaN(targetHeight) ? '100%' : `${targetHeight}px`);

                targetEl.style.setProperty('--video-scale', scale);
                targetEl.style.setProperty('--video-x-offset', `${xOffset * 100}%`);
                targetEl.style.setProperty('--video-y-offset', `${yOffset * 100}%`);
                targetEl.style.setProperty('--video-clip-width', `${clipWidth * 100}%`);
                targetEl.style.setProperty('--video-clip-height', `${clipHeight * 100}%`);

                if (!region) {
                    controlsOverlay.style.width = `${targetWidth}px`;
                    controlsOverlay.style.height = `${targetHeight}px`;
                }
            }
        }
        catch (e) { }
    }

    /** @private */
    #handleRegionKeydown(event) {
        if (event.keyCode == 13 || event.keyCode == 32) {
            event.preventDefault();
            event.stopPropagation();
            this.#handleRegionClick(event);
        }
    }

    /** @private */
    #handleRegionClick(event) {
        this.lastFocused = document.activeElement;
        this.activeRegion = JSON.parse(event.target.dataset.region);
        const region = this.activeRegion;

        this.zoom(region.left, region.top, region.width, region.height);
    }

    /** @private */
    #handleClickToExitKeydown(event) {
        if (event.keyCode == 13 || event.keyCode == 32) {
            event.preventDefault();
            event.stopPropagation();
            this.#handleClickToExit(event);
        }
    }

    /**
     * @private
     * @param {string} id
     */
    #handleRegionStart(id) {
        this.unzoom();

        const el = _controlsOverlay.get(this).querySelector(`.mediasite-player__smartzoom-region[data-id="${id}"]`);
        if (el) {
            el.style.display = '';
        }
    }

    /**
     * @private
     * @param {string} id
     */
    #handleRegionEnd(id) {
        this.unzoom();

        const el = _controlsOverlay.get(this).querySelector(`.mediasite-player__smartzoom-region[data-id="${id}"]`);
        if (el) {
            el.style.display = 'none';
        }
    }

    /** @private */
    #handleClickToExit(event) {
        this.unzoom();
    }

    /** @private */
    #moveComponentBeforeVideo() {
        try {
            // Note: This can fail on iOS Safari because the <video> element gets replaced during init
            //       and addChild() still references the old video element when trying to insert a child
            //       at a specific index
            const component = _wrapper.get(this);
            this.player.layout().addChild(component, {}, 0);
        }
        catch (e) { }
    }

    /** @private */
    #moveComponentAfterVideo() {
        const component = _wrapper.get(this);
        const indexBeforeTech = this.player.layout().children().length - 1;
        this.player.layout().addChild(component, {}, indexBeforeTech);
    }

    /**
     * 
     * @param {Object} region
     * @param {number} region.left The X coordinate of the region with respect to the overall dimensions of the presentation media.
     * @param {number} region.top The Y coordinate of the region with respect to the overall dimensions of the presentation media.
     * @param {number} region.width The width of the region with respect to the overall dimensions of the presentation media.
     * @param {number} region.height The height of the region with respect to the overall dimensions of the presentation media.
     * @param {number} region.time The time in seconds at which the clickable region should become available to the user.
     * @param {number} region.duration A number of seconds for which the clickable region should remain available to the user after it has appeared.
     * @param {string} [region.id] A string by which to identify the region. If none is provided, one will be generated dynamically.
     * @returns {string} The ID value that was passed in the options object, or a dynamically generated ID if one was not specified.
     * @example
     * const regionId = player.smartzoom.addRegion({
     *     left: 0,
     *     top: 0,
     *     width: 0.5,
     *     height: 0.5,
     *     time: 0,
     *     duration: 10
     * });
     */
    addRegion(region) {
        const { left, top, width, height, time, duration, id = uuid() } = region;

        if (time !== undefined && duration !== undefined) {
            // Create a timed event. Cutting the duration short by 1s gives us a chance to
            // anticipate the change and reset zoom during playback.
            this.player.timedEvents().add(
                { id, time, duration: duration - 1, type: 'hidden' },
                (...args) => this.#handleRegionStart(...args),
                (...args) => this.#handleRegionEnd(...args)
            );
        }

        const rect = document.createElement('div');
        rect.classList.add('mediasite-player__smartzoom-region');
        rect.style.left = `${left * 100}%`;
        rect.style.top = `${top * 100}%`;
        rect.style.width = `${width * 100}%`;
        rect.style.height = `${height * 100}%`;
        rect.addEventListener('click', (...args) => this.#handleRegionClick(...args));
        rect.addEventListener('keydown', (...args) => this.#handleRegionKeydown(...args));
        rect.dataset.region = JSON.stringify(region);
        rect.dataset.id = id;

        rect.setAttribute('role', 'button');
        rect.setAttribute('tabindex', 0);
        rect.setAttribute('aria-label', 'Click to expand');

        _controlsOverlay.get(this).appendChild(rect);

        const currentTime = this.player.currentTime();
        if (currentTime < time || currentTime > (time + duration)) {
            this.#handleRegionEnd(id);
        }

        this.#incrementRegionCount();

        this.log(`Added region: ${JSON.stringify(region)}`);

        return id;
    }

    /**
     * Removes the clickable region with the given ID.
     * @param {string} id The ID of the region to remove.
     * @example
     * player.smartzoom.removeRegion('regionId');
     */
    removeRegion(id) {
        if (id) {
            const smartzoomContainer = _controlsOverlay.get(this);
            for (let i = 0; i < smartzoomContainer.children.length; i++) {
                const current = smartzoomContainer.children[i];
                if (current.dataset?.id === id) {
                    current.remove();
                    break;
                }
            }
            this.player.timedEvents().remove(id);
            this.#decrementRegionCount();
        }
    }

    /**
     * Assigns size and location to the clickable region with the given ID.
     * @param {string} id The ID of the region to remove.
     * @param {object} data The left, top, height, and width values to assign to the region.
     * @example
     * player.smartzoom.moveRegion('regionId', rect);
     */
    moveRegion(id, data) {
        if (!id || !data) {
            return;
        }

        const { left, top, width, height } = data;
        const el = _controlsOverlay.get(this).querySelector(`.mediasite-player__smartzoom-region[data-id="${id}"]`);
        if (el) {
            el.style.left = `${left * 100}%`;
            el.style.top = `${top * 100}%`;
            el.style.width = `${width * 100}%`;
            el.style.height = `${height * 100}%`;
            el.dataset.region = JSON.stringify(data);
        }
    }

    /** @private */
    #incrementRegionCount() {
        this.regionCount += 1;
        this.#reactToRegionCountChange();
    }

    /** @private */
    #decrementRegionCount() {
        this.regionCount -= 1;
        this.#reactToRegionCountChange();
    }

    /** @private */
    #reactToRegionCountChange() {
        if (this.regionCount > 1) {
            this.player.addClass('mediasite-player--smartzoom-enabled');
        } else {
            this.player.removeClass('mediasite-player--smartzoom-enabled');
        }
    }

    /**
     * Zooms and crops the video to only show the specified area. Measurements should be values between 0.0 and 1.0. This does not necessarily have to correspond to a smartzoom region added via {@link SmartzoomPlugin#addRegion}, and can in fact be used to zoom into arbitrary portions of the video at will.
     * @param {number} x An X coordinate with respect to the overall dimensions of the presentation media.
     * @param {number} y A Y coordinate with respect to the overall dimensions of the presentation media.
     * @param {number} width A width with respect to the overall dimensions of the presentation media.
     * @param {number} height A height with respect to the overall dimensions of the presentation media.
     * @example
     * player.smartzoom.zoom(0, 0, 0.5, 0.5);
     */
    zoom(x, y, width, height) {
        this.activeRegion = { left: x, top: y, width, height };
        const region = this.activeRegion;

        // Get the original onscreen dimensions
        const targetEl = _target.get(this);
        const canvasEl = _canvas.get(this);
        const targetBounds = targetEl.getBoundingClientRect();
        const targetAspect = _aspect.get(this);

        const targetWidth = targetBounds.width;
        const targetHeight = targetBounds.height;

        const regionAspect = (region.width * targetWidth) / (region.height * targetHeight);

        // Trigger resize so video is in its new position
        this.#handlePlayerResize();

        // Get the new onscreen dimensions
        const newBounds = targetEl.getBoundingClientRect();
        const scale = newBounds.width / targetBounds.width;

        // Set up animation starting values
        const videoStartTransition = '';
        const videoStartTransform = `scale(${1 / scale}) translate(-50%, -50%)`;
        const canvasStartVisibility = 'visible';
        const canvasStartTransition = '';
        const canvasStartOpacity = 1;

        // Set up animation ending values
        const videoEndTransition = 'transform .2s ease';
        const videoEndTransform = '';
        const canvasEndVisibility = 'hidden';
        let canvasEndTransition = 'visibility 0s linear .2s';
        let canvasEndOpacity = canvasStartOpacity;

        if (targetAspect !== regionAspect) {
            canvasEndTransition = 'opacity .2s ease, visibility 0s linear .2s';
            canvasEndOpacity = 0;
        }

        // Apply starting values
        this.#moveComponentBeforeVideo();
        targetEl.style.transition = videoStartTransition;
        targetEl.style.transform = videoStartTransform;

        canvasEl.style.visibility = canvasStartVisibility;
        canvasEl.style.transition = canvasStartTransition;
        canvasEl.style.opacity = canvasStartOpacity;
        canvasEl.style.width = `${targetBounds.width}px`;
        canvasEl.style.height = `${targetBounds.height}px`;
        drawFrame(_canvas.get(this), targetEl, 0, 0, 1, 1);

        // Force layout
        void (targetEl.offsetHeight);

        // Apply ending values and listen for transitionend
        const transitionCleanup = () => {
            targetEl.removeEventListener('transitionend', transitionCleanup);
            targetEl.style.transition = '';
            canvasEl.style.transition = '';
            canvasEl.style.visibility = '';
            canvasEl.style.width = '';
            canvasEl.style.height = '';
            canvasEl.style.opacity = '';
            this.#moveComponentAfterVideo();

            // Focus "exit" button when transition finishes
            _clickToExit.get(this).querySelector('button')?.focus();
        }

        targetEl.addEventListener('transitionend', transitionCleanup);
        targetEl.style.transition = videoEndTransition;
        targetEl.style.transform = videoEndTransform;

        canvasEl.style.transition = canvasEndTransition;
        canvasEl.style.opacity = canvasEndOpacity;
        canvasEl.style.visibility = canvasEndVisibility;

        // Hide clickable regions and show the "exit" button
        const smartzoomContainer = _controlsOverlay.get(this);
        smartzoomContainer.style.setProperty('display', 'none');
        _clickToExit.get(this).hidden = false;

        this.log(`Zoomed in on region: ${JSON.stringify(region)}}`);
        this.player.toggleClass('mediasite-player--smartzoom-zoomed');
    }

    /**
     * Resets the zoom and crop levels back to their default so the entire video is visible.
     * @example
     * player.smartzoom.unzoom();
     */
    unzoom(immediateRezoom) {
        const region = this.activeRegion;

        if (region) {
            this.activeRegion = null;

            // Get the original onscreen dimensions
            const targetEl = _target.get(this);
            const canvasEl = _canvas.get(this);
            const targetBounds = targetEl.getBoundingClientRect();
            const targetAspect = _aspect.get(this);

            const targetWidth = targetBounds.width;
            const targetHeight = targetBounds.height;

            const regionAspect = (region.width * targetWidth) / (region.height * targetHeight);
            const regionOriginalX = targetBounds.left + (region.left * targetBounds.width);
            const regionOriginalY = targetBounds.top + (region.top * targetBounds.height);
            const regionOriginalWidth = targetBounds.width * region.width;

            // Trigger resize so video is in its new position
            this.#handlePlayerResize();

            // Get the new onscreen dimensions
            const newBounds = targetEl.getBoundingClientRect();
            const regionNewX = newBounds.left + (region.left * newBounds.width);
            const regionNewY = newBounds.top + (region.top * newBounds.height);
            const regionNewWidth = newBounds.width * region.width;
            const regionNewHeight = newBounds.height * region.height;

            // Set up animation starting values
            const canvasStartVisibility = 'visible';
            const canvasStartTransition = '';
            const canvasStartOpacity = 1;
            const canvasStartMargin = 0;
            const canvasStartPosition = 'fixed';

            const translation = `translate(${regionOriginalX - regionNewX}px, ${regionOriginalY - regionNewY}px)`;
            const canvasStartTransform = `${translation} scale(${regionOriginalWidth / regionNewWidth})`;

            const videoStartTransition = '';
            const videoStartOpacity = 0;

            // Set up animation ending values
            const canvasEndTransition = `opacity .05s ease .15s, transform .2s ease, visibility 0s linear .2s`;
            const canvasEndTransform = null;
            const canvasEndOpacity = 0;
            const canvasEndVisibility = 'hidden';

            const videoEndOpacity = 1;
            let videoEndTransition = videoStartTransition;
            if (targetAspect !== regionAspect) {
                videoEndTransition = 'opacity .2s ease';
            }

            // Apply starting values
            this.#moveComponentAfterVideo();
            canvasEl.style.transition = canvasStartTransition;
            canvasEl.style.transform = canvasStartTransform;
            canvasEl.style.opacity = canvasStartOpacity;
            canvasEl.style.visibility = canvasStartVisibility;
            targetEl.style.transition = videoStartTransition;
            targetEl.style.opacity = videoStartOpacity;
            
            canvasEl.style.margin = canvasStartMargin;
            canvasEl.style.position = canvasStartPosition;
            canvasEl.style.left = `${regionNewX}px`;
            canvasEl.style.top = `${regionNewY}px`;
            canvasEl.style.width = `${regionNewWidth}px`;
            canvasEl.style.height = `${regionNewHeight}px`;
            drawFrame(canvasEl, targetEl, region.left, region.top, region.width, region.height);
            
            // Force layout
            void (targetEl.offsetHeight);

            // Apply ending values and listen for transitionend
            const transitionCleanup = () => {
                canvasEl.removeEventListener('transitionend', transitionCleanup);
                canvasEl.style.transition = '';
                canvasEl.style.left = '';
                canvasEl.style.top = '';
                canvasEl.style.width = '';
                canvasEl.style.height = '';
                canvasEl.style.margin = '';
                canvasEl.style.visibility = '';
                canvasEl.style.opacity = '';
                canvasEl.style.position = '';

                targetEl.style.opacity = '';
                targetEl.style.transition = '';

                if (!immediateRezoom) {
                    // Manage focus for accessibility
                    this.lastFocused?.focus();
                    this.lastFocused = null;
                }
            }

            canvasEl.addEventListener('transitionend', transitionCleanup);
            canvasEl.style.transition = canvasEndTransition;
            canvasEl.style.transform = canvasEndTransform;
            canvasEl.style.opacity = canvasEndOpacity;
            canvasEl.style.visibility = canvasEndVisibility;

            targetEl.style.transition = videoEndTransition;
            targetEl.style.opacity = videoEndOpacity;

            // Show clickable regions and hide the "exit" button
            const smartzoomContainer = _controlsOverlay.get(this);
            smartzoomContainer.style.display = null;
            _clickToExit.get(this).hidden = true;

            this.log(`Zoomed out from region: ${JSON.stringify(region)}`);
            this.player.toggleClass('mediasite-player--smartzoom-zoomed');
        }
    }

    dispose() {
        window.removeEventListener('resize', this.windowResizeListener);
        this.unzoom();

        if (this.player.usingPlugin('timedEvents')) {
            const smartzoomContainer = _controlsOverlay.get(this);
            for (let i = 0; i < smartzoomContainer.children.length; i++) {
                const current = smartzoomContainer.children[i];
                this.player.timedEvents().remove(current.dataset.id);
            }
        }

        const wrapper = _wrapper.get(this);
        wrapper.dispose();
        this.player.layout().removeChild(wrapper);

        super.dispose();
    }
}

function drawFrame(canvas, targetEl, x, y, width, height) {
    const targetWidth = targetEl.videoWidth ?? 0;
    const targetHeight = targetEl.videoHeight ?? 0;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // try/catch in case of error drawing due to memory, CORS, etc.
    try {
        const context = canvas.getContext('2d');
        context.drawImage(targetEl, targetWidth * x, targetHeight * y, targetWidth * width, targetHeight * height, 0, 0, targetWidth, targetHeight);
    }
    catch (e) {

    }
}

export default SmartzoomPlugin;
